import { CdkDragDrop, moveItemInArray } from '@angular/cdk/drag-drop';
import { Component, Inject, OnInit } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { TextBank } from '@authority-labs/api-interfaces';
import { TextTypeEnum } from '@authority-labs/enums';
import { nameof } from '@authority-labs/helpers';
import { RequestQueryBuilder } from '@nestjsx/crud-request';
import { NzMessageService } from 'ng-zorro-antd/message';
import { TextBankService } from '../../services';

@Component({
  templateUrl: './notes-bank-dialog.component.html',
  styleUrls: ['./notes-bank-dialog.component.scss']
})
export class NotesBankDialogComponent implements OnInit {
  title = 'בנק הערות';
  notes: TextBank[] = [];
  newNote: TextBank;
  addNew = false;
  editRowIndex = -1;
  submitting = false;
  isLoading = true;
  selected: TextBank;
  type: TextTypeEnum | string;
  multi: boolean;
  supportDefault = false;
  setOfCheckedId = new Set<number>();
  prevDefaultItemId: number;

  get isEditing(): boolean {
    return this.editRowIndex !== -1;
  }

  constructor(
    public dialogRef: MatDialogRef<string>,
    @Inject(MAT_DIALOG_DATA)
    data: { type: string; multi: boolean; supportDefault: boolean },
    private readonly textBankService: TextBankService,
    private readonly message: NzMessageService
  ) {
    this.type = data.type;
    this.multi = data.multi;
    this.supportDefault = data.supportDefault;
  }

  ngOnInit(): void {
    const qb = RequestQueryBuilder.create();
    qb.search({
      [nameof<TextBank>('type')]: {
        $eq: this.type
      }
    });
    qb.sortBy({ field: 'sortOrder', order: 'ASC' });

    this.textBankService.getAll(qb).subscribe((resp) => {
      this.isLoading = false;
      this.notes = resp;
    });
  }

  add() {
    this.addNew = true;
    this.newNote = {
      content: '',
      type: this.type,
      isDefault: false,
      sortOrder: 0
    };
  }

  save(item: TextBank): void {
    this.submitting = true;

    if (this.newNote.isDefault && !item.isDefault) {
      const note = this.notes.find((x) => x.isDefault && x.id !== item.id);

      if (note) {
        this.prevDefaultItemId = note.id;
        note.isDefault = false;
      }
    }

    item.content = this.newNote.content;
    item.isDefault = this.newNote.isDefault;

    const noteItem: TextBank = {
      content: item.content,
      type: item.type,
      isDefault: item.isDefault
    };

    const sub = item.id
      ? this.textBankService.updateOne(item.id, noteItem)
      : this.textBankService.createOne(noteItem);

    sub.subscribe(this.onSaveSuccess, this.onSaveError);
  }

  canEdit(ind: number): boolean {
    return !this.addNew && (!this.isEditing || this.editRowIndex === ind);
  }

  cancel(): void {
    this.addNew = false;
    this.editRowIndex = -1;
  }

  edit(ind: number): void {
    this.newNote = { ...this.notes[ind] };
    this.editRowIndex = ind;
  }

  delete(item: TextBank): void {
    this.submitting = true;
    this.textBankService
      .deleteOne(item.id)
      .subscribe(() => this.onDeleteSuccess(item.id), this.onDeleteError);
  }

  onSelect(item: TextBank): void {
    this.selected = item;
  }

  chooseOneAndClose(item: TextBank): void {
    this.dialogRef.close(item.content);
  }

  chooseAndClose(): void {
    const items = this.notes
      .filter((note) => this.setOfCheckedId.has(note.id))
      .map((note) => note.content);
    this.dialogRef.close(items);
  }

  drop(event: CdkDragDrop<TextBank[]>): void {
    moveItemInArray(this.notes, event.previousIndex, event.currentIndex);
    this.notes = [...this.notes];
    this.persistSortOrder();
  }

  onItemChecked(id: number, checked: boolean): void {
    this.updateCheckedSet(id, checked);
  }

  private onSaveSuccess = (item: TextBank) => {
    const note = this.notes.find((n) => n.id === item.id);
    if (note) {
      note.content = item.content;
    } else {
      this.notes.unshift(item);
      this.persistSortOrder();
    }
    this.submitting = false;
    this.cancel();
    this.message.create('success', 'הפעולה נשמרה בהצלחה');

    if (this.prevDefaultItemId) {
      this.textBankService
        .updateOne(this.prevDefaultItemId, {
          isDefault: false
        })
        .subscribe(() => (this.prevDefaultItemId = null));
    }
  };

  private onSaveError = () => {
    this.submitting = false;
    this.message.create('error', 'הפעולה נכשלה. סטטוס כבר קיים');
  };

  private onDeleteSuccess = (itemId: number) => {
    const ind = this.notes.findIndex((note) => note.id === itemId);
    this.notes.splice(ind, 1);
    this.submitting = false;
    this.cancel();
    this.message.create('success', 'הפעולה נשמרה בהצלחה');
  };

  private onDeleteError = () => {
    this.submitting = false;
    this.message.create('error', 'הפעולה נכשלה. הסטטוס נמצא בשימוש');
  };

  private persistSortOrder(): void {
    const items = this.notes
      .filter((note) => note.id && note.sortOrder !== this.notes.indexOf(note))
      .map((note) => {
        const sortOrder = this.notes.indexOf(note);
        note.sortOrder = sortOrder;
        return { id: note.id, sortOrder };
      });

    if (items.length) {
      this.textBankService.updateSortOrder(items).subscribe();
    }
  }

  private updateCheckedSet(id: number, checked: boolean): void {
    if (checked) {
      this.setOfCheckedId.add(id);
    } else {
      this.setOfCheckedId.delete(id);
    }
  }
}
